import React, { useEffect, useMemo } from 'react'
import { EmptyState, Search } from '@enamelai/design-system'
import type { PatientProcedureEntryWithId } from './mockData'
import './PatientFormsViewSidebar.css'

type PatientFormsViewSidebarProps = {
  procedures: PatientProcedureEntryWithId[]
  selectedProcedureId: string | null
  onSelectProcedure: (id: string) => void
  sidebarSearchQuery: string
  onSidebarSearch: (value: string) => void
}

const PatientFormsViewSidebar: React.FC<PatientFormsViewSidebarProps> = ({
  procedures,
  selectedProcedureId,
  onSelectProcedure,
  sidebarSearchQuery,
  onSidebarSearch,
}) => {
  const filteredProcedures = useMemo(() => {
    const sortedProcedures = [...procedures].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
    )
    if (!sidebarSearchQuery.trim()) return sortedProcedures
    const q = sidebarSearchQuery.toLowerCase()
    return sortedProcedures.filter(
      (proc) =>
        proc.procedureName.toLowerCase().includes(q) ||
        proc.date.toLowerCase().includes(q),
    )
  }, [procedures, sidebarSearchQuery])

  useEffect(() => {
    if (!filteredProcedures.length) return
    const hasSelectedProcedure = filteredProcedures.some(
      (procedure) => procedure.id === selectedProcedureId,
    )
    if (!hasSelectedProcedure) {
      onSelectProcedure(filteredProcedures[0].id)
    }
  }, [filteredProcedures, onSelectProcedure, selectedProcedureId])

  return (
    <aside className="pfv-sidebar patient-forms-view-sidebar">
      <div className="pfv-sidebar-title">Appointments</div>
      <Search
        className="pfv-sidebar-search"
        placeholder="Search"
        ariaLabel="Search appointments"
        value={sidebarSearchQuery}
        onChange={onSidebarSearch}
        onClear={() => onSidebarSearch('')}
      />
      {filteredProcedures.length === 0 ? (
        <EmptyState
          className="pfv-sidebar-empty"
          title="No appointments found"
          description="Try a different search."
          outlined
        />
      ) : (
        <div className="pfv-sidebar-list">
          {filteredProcedures.map((proc) => {
            const isActive = proc.id === selectedProcedureId
            return (
              <button
                type="button"
                key={proc.id}
                className={`pfv-sidebar-item${isActive ? ' pfv-sidebar-item--active' : ''}`}
                aria-current={isActive ? 'true' : undefined}
                onClick={() => onSelectProcedure(proc.id)}
              >
                <span className="pfv-sidebar-item-name">
                  {proc.procedureName}
                </span>
                <span className="pfv-sidebar-item-date">{proc.date}</span>
              </button>
            )
          })}
        </div>
      )}
    </aside>
  )
}

export default PatientFormsViewSidebar
